import { CircleFragment, Visibility_Enum } from '../gql'
import { OrgData } from './OrgData'

// CONF-02 — the three visibility levels, as the SCREEN needs them.
//
// ⚠️ Nothing here is a security rule. Who may read or modify an object is
// decided by the Hasura permissions, written from the single model in
// docs/audits/scripts/regle-visibilite.py. This module only lets a screen say
// the same thing in words — which circles to name in a sentence, whether to
// grey out an action — so it must mirror that model, never extend it.
//
// Decisions: docs/features/conf-02-harmonisation-visibilite.md

export type VisibleObjectType =
  | 'thread'
  | 'task'
  | 'meeting'
  | 'meetingRecurring'
  | 'decision'

// The level an object is born with (8th round, 15/09/2026): Circle, except a
// decision ("policy" on screen), which binds beyond the circle adopting it and
// stays readable by the whole organisation. The first rounds kept the whole
// organisation for projects and meetings too, as the old "not private" meant;
// at the UdN it showed every member the meetings of circles they had no part
// in. Same values as the column defaults in the database, for objects created
// without the screens (calendar import).
export const defaultVisibility: Record<VisibleObjectType, Visibility_Enum> = {
  thread: Visibility_Enum.Circle,
  task: Visibility_Enum.Circle,
  meeting: Visibility_Enum.Circle,
  meetingRecurring: Visibility_Enum.Circle,
  decision: Visibility_Enum.Org,
}

// Circles whose participants read an object at the Circle level: the carrier,
// its parent, and its grandparent when the carrier is a link role.
// TypeScript mirror of the `circle_reader` SQL view (4th round: a circle is a
// role of the circle above it).
export function getCircleLevelCircles(
  orgData: OrgData,
  circleId: string
): CircleFragment[] {
  const carrier = orgData.getCircle(circleId)
  if (!carrier) return []
  const result = [carrier]
  const parent = orgData.getCircle(carrier.parentId ?? undefined)
  if (!parent) return result
  result.push(parent)
  const grandparent = orgData.getCircle(parent.parentId ?? undefined)
  if (grandparent && orgData.getRole(carrier.roleId)?.parentLink) {
    result.push(grandparent)
  }
  return result
}

export interface VisibilityNames {
  // The role or circle carrying the object.
  carrier: string
  // Who reads at the Circle level, named as circles. A leaf role is left out
  // when it has a parent: its holders are participants of that parent, and
  // "the members of Treasury and Finance" would read as two groups.
  circles: string[]
  // The circles that read at the Circle level but NOT at the Role level: the
  // ones a sentence must name when it says who will not see the object.
  others: string[]
}

export function getVisibilityNames(
  orgData: OrgData,
  circleId: string
): VisibilityNames {
  const circles = getCircleLevelCircles(orgData, circleId)
  const [carrier, ...above] = circles
  const nameOf = (circle: CircleFragment) =>
    orgData.getRole(circle.roleId)?.name ?? ''
  if (!carrier) return { carrier: '', circles: [], others: [] }
  const carrierIsLeaf = orgData.childrenOf(carrier.id).length === 0
  const named = carrierIsLeaf && above.length > 0 ? above : circles
  return {
    carrier: nameOf(carrier),
    circles: named.map(nameOf),
    others: above.map(nameOf),
  }
}

export interface VisibilityAccessInput {
  orgData: OrgData
  circleId: string
  visibility: Visibility_Enum
  memberId?: string
  // Members named on the object: extra members, attendees, assignee.
  namedMemberIds?: readonly string[]
  // Whether the named members may also modify (false for a read-only invite).
  namedCanWrite?: boolean
  // Whether the member's organisation role writes at all (not Readonly).
  isOrgWriter: boolean
}

export interface VisibilityAccess {
  canRead: boolean
  canWrite: boolean
}

// Same branches as the model, for display: participant ∨ named ∨ (Org) ∨
// (≠ Role ∧ reader of the circle). Writing drops the Org branch (D8).
export function getVisibilityAccess({
  orgData,
  circleId,
  visibility,
  memberId,
  namedMemberIds = [],
  namedCanWrite = true,
  isOrgWriter,
}: VisibilityAccessInput): VisibilityAccess {
  if (!memberId) return { canRead: false, canWrite: false }
  const participates = (id: string) =>
    orgData.getParticipants(id).some((p) => p.member.id === memberId)

  const isParticipant = participates(circleId)
  const isNamed = namedMemberIds.includes(memberId)
  const isCircleReader =
    visibility !== Visibility_Enum.Role &&
    getCircleLevelCircles(orgData, circleId).some((c) => participates(c.id))

  return {
    canRead:
      isParticipant ||
      isNamed ||
      isCircleReader ||
      visibility === Visibility_Enum.Org,
    canWrite:
      isParticipant ||
      (isNamed && namedCanWrite) ||
      (isCircleReader && isOrgWriter),
  }
}
